import React, { useEffect, useMemo, useState, useRef } from "react";
import "../styles/styles.css";

const STEPS = [
  {
    id: null,
    title: "welcome to the arqive!",
    text: "This is a living, digital map of user-generated queer histories, herstories, and theirstories — created by and for queer people around the world. This tutorial will show you how you can start getting involved!",
    tab: "search"
  },
  {
    id: "tutorial-search",
    title: "search",
    text: "Looking for a particular story, person, or moment? This is where you can enter a keyword and begin exploring the map.",
    tab: "search"
  },
  {
    id: "tutorial-category",
    title: "category",
    text: "Personal stories are shared by individuals, whereas community stories are shared (and editable) by everyone on The arqive.",
    tab: "search"
  },
  {
    id: "tutorial-date-range",
    title: "date range",
    text: "Use the date range to trace stories across time — our presence spans all of human existence.",
    tab: "search"
  },
  {
    id: "tutorial-apply",
    title: "apply",
    text: "Click Apply to update the results based on your filters.",
    tab: "search"
  },

  // TRANSITION TO ADD STORY TUTORIAL

  {
    id: null,
    title: "add a story",
    text: "You’ve seen how stories are discovered across the map. Now, you can add your own!",
    action: "goAddTab",
    tab: "add"
  },
  { 
    id: "tutorial-add-title", 
    title: "name of the story", 
    text: "Give your story a title. This can mark a moment, a place, a memory, or a person.",
    tab: "add"
  },
  { 
    id: "tutorial-add-description", 
    title: "what happened?", 
    text: "Spill the tea. You can share experiences, memories, advice, or just the facts.",
    tab: "add"
  },
  { 
    id: "tutorial-add-category", 
    title: "category", 
    text: "Is your story personal to you, or is it a shared story with the community? Think of community stories like a public forum or wiki article, whereas personal stories are more of a diary or journal.",
    tab: "add"
  },
  { 
    id: "tutorial-add-dates", 
    title: "when", 
    text: "Add the date or approximate time period. Mapping our stories across time provides context and searchability.",
    tab: "add"
  },
  { 
    id: "tutorial-add-address", 
    title: "where", 
    text: "Search for an address or input your own. If you’re worried about being too specific, it’s okay to be more general in your placement.",
    tab: "add"
  },
  { 
    id: "tutorial-submit", 
    title: "submit", 
    text: "When you’re ready, click submit to share your story with the world and be an active member of our community.",
    tab: "add"
  },

];

export default function SearchStoryTour({ open, onClose, setActiveTab }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [isMeasuring, setIsMeasuring] = useState(false);

  const step = useMemo(() => STEPS[stepIndex], [stepIndex]);
  const prevTabRef = useRef(null);
  const [retryTick, setRetryTick] = useState(0);

  const waitFrames = (n = 3) =>
  new Promise((resolve) => {
    const step = () => (n-- <= 0 ? resolve() : requestAnimationFrame(step));
    requestAnimationFrame(step);
  });

  const waitForElement = async (id, maxFrames = 20) => {
    for (let i = 0; i < maxFrames; i++) {
      const el = document.getElementById(id);
      if (el) return el;
      await new Promise((r) => requestAnimationFrame(r));
    }
    return null;
  };

  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!setActiveTab) return;

    const step = STEPS[stepIndex];
    if (!step?.tab) return;

    setActiveTab(step.tab); // search or add tab based on the step

  }, [open, stepIndex, setActiveTab]);

  useEffect(() => {
    if (!open) return;

    const run = async () => {
      // Track tab EVERY step (even welcome steps)
      const currentTab = step?.tab;
      const tabJustSwitched =
        prevTabRef.current && prevTabRef.current !== currentTab;
      prevTabRef.current = currentTab;

      // Welcome/transition step: no target
      if (!step?.id) {
        setIsMeasuring(false);
        return;
      }

      setIsMeasuring(true);

      const el = await waitForElement(step.id, tabJustSwitched ? 80 : 40);
      if (!el) {
        setTimeout(() => {
          setRetryTick((t) => t + 1);
        }, 150);

        setIsMeasuring(false);
        return;
      }

      el.scrollIntoView({ behavior: "auto", block: "center" });

      const settleFrames = tabJustSwitched ? 10 : 2;
      for (let i = 0; i < settleFrames; i++) {
        await new Promise((r) => requestAnimationFrame(r));
      }

      setIsMeasuring(false);
    };

    run();
  }, [open, stepIndex, retryTick]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose(); // same behavior as clicking the red X
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const target = step.id ? document.getElementById(step.id) : null;
  const rect = (!isMeasuring && target) ? target.getBoundingClientRect() : null;

  const spotlightStyle = !isMeasuring && rect
  ? {
      position: "fixed",
      left: rect.left - 8,
      top: rect.top - 8,
      width: rect.width + 16,
      height: rect.height + 16,
      borderRadius: "12px",
      boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
      border: "2px solid rgba(255,255,255,0.9)",
      zIndex: 10000,
      pointerEvents: "none",
    }
  : null;

  const tooltipStyle = rect
    ? {
        position: "fixed",
        left: Math.min(rect.left, window.innerWidth - 360),
        top: Math.min(rect.bottom + 12, window.innerHeight - 180),
        width: 340,
        zIndex: 10001,
        background: "white",
        borderRadius: 12,
        padding: 14,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
      }
    : {
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: 340,
        zIndex: 10001,
        background: "white",
        borderRadius: 12,
        padding: 14,
      };

  const blockerStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: spotlightStyle ? "transparent" : "rgba(0,0,0,0.65)",
    pointerEvents: "auto",
  };

  return (
    <>
      <div
        style={blockerStyle}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onWheel={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {spotlightStyle && <div style={spotlightStyle} />}

      {!isMeasuring && (
        <div style={tooltipStyle}>
          <div className="tour-title" style={{ marginBottom: 6 }}>
            {step.title}{" "}
            <span>
              ({stepIndex + 1}/{STEPS.length})
            </span>
          </div>
          <div className="tour-body" style={{ marginBottom: 12 }}>
            {step.text}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
          <button
            className="tour-btn"
            type="button"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={stepIndex === 0}
            style={{
              background: "rgb(77, 65, 133)",
              border: "none",
              color: "white",
              padding: "8px 12px",
              borderRadius: 8,
              cursor: "pointer",
              background: stepIndex === 0 ? "rgba(77, 65, 133, 0.5)" : "rgb(77, 65, 133)",
            }}
          >
            back
          </button>

          <button
            type="button"
            onClick={onClose}
            aria-label="End tutorial"
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              background: "transparent",
              border: "none",
              color: "#ff4d4f",
              fontSize: 20,
              fontWeight: 700,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {stepIndex < STEPS.length - 1 ? (
            <button 
              className="tour-btn" 
              type="button" 
              onClick={() => setStepIndex((i) => i + 1)}
              style={{
                background: "rgb(77, 65, 133)",
                border: "none",
                color: "white",
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              next
            </button>
          ) : (
            <button 
              className="tour-btn" 
              type="button" 
              onClick={onClose}
              style={{
                background: "rgb(77, 65, 133)",
                border: "none",
                color: "white",
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              finish
            </button>
          )}
        </div>
      </div>
      )}
    </>
  );
}